import { supabase } from "./supabase";
import { BUCKET_PIECES_JUSTIFICATIVES } from "./piecesJustificativesStorage";
import { formatCompte8 } from "./comptable/formatCompte";
import { genererEcritureTransactionDepenseRapprochee, genererEcritureTresorerieDirecteValidee } from "./comptable";
import { logger } from "./logger";

/** Message lisible pour erreurs Supabase / RPC (souvent pas des `instanceof Error`). */
export function errorMessageFromUnknown(e: unknown): string {
  if (e instanceof Error && e.message.trim()) return e.message;
  if (e && typeof e === "object") {
    const o = e as { message?: unknown; details?: unknown; hint?: unknown };
    if (typeof o.message === "string" && o.message.trim()) return o.message;
    if (typeof o.details === "string" && o.details.trim()) return o.details;
    if (typeof o.hint === "string" && o.hint.trim()) return o.hint;
  }
  if (typeof e === "string" && e.trim()) return e;
  return "Erreur inconnue";
}

export type ModeTraitement = "facture" | "tresorerie_directe";

export type JustificatifStatut = "en_attente" | "fourni" | "non_requis";

export function normalizeJustificatifStatut(v: string | null | undefined): JustificatifStatut {
  if (v === "fourni" || v === "non_requis" || v === "en_attente") return v;
  return "en_attente";
}
/** Sorties : frais bancaires, etc. Entrées : apports, subventions, prêt reçu, etc. (`virement_interne` et `autre` peuvent être les deux sens). */
export type TypeTresorerieDirecte =
  | "frais_bancaires"
  | "agios"
  | "commissions"
  | "virement_interne"
  | "autre"
  | "apport_capital"
  | "apport_compte_courant"
  | "subvention_aide"
  | "pret_bancaire";

export interface TresoreriePresetRow {
  label: string;
  compte: string;
  compteLibelle: string;
  libelle: string;
  tauxTva: number;
}

/** Présets sortie (D charge / C trésorerie). */
export const TRESORERIE_PRESETS_SORTIE: Record<
  "frais_bancaires" | "agios" | "commissions" | "virement_interne" | "autre",
  TresoreriePresetRow
> = {
  frais_bancaires: { label: "Frais bancaires", compte: "631000", compteLibelle: "Frais bancaires", libelle: "Frais bancaires", tauxTva: 0 },
  agios: { label: "Agios", compte: "631700", compteLibelle: "Frais sur instruments monnaie électronique", libelle: "Agios", tauxTva: 0 },
  commissions: { label: "Commissions", compte: "631800", compteLibelle: "Autres frais bancaires", libelle: "Commissions", tauxTva: 0 },
  virement_interne: { label: "Virement interne", compte: "585000", compteLibelle: "Virements de fonds", libelle: "Virement interne", tauxTva: 0 },
  autre: { label: "Autre", compte: "588000", compteLibelle: "Autres virements internes", libelle: "Opération trésorerie", tauxTva: 0 },
};

/** Présets entrée (D trésorerie / C contrepartie). */
export const TRESORERIE_PRESETS_ENTREE: Record<
  "apport_capital" | "apport_compte_courant" | "subvention_aide" | "pret_bancaire" | "virement_interne" | "autre",
  TresoreriePresetRow
> = {
  apport_capital: { label: "Apport en capital", compte: "1013", compteLibelle: "Capital souscrit, appelé, versé", libelle: "Apport en capital", tauxTva: 0 },
  apport_compte_courant: { label: "Apport compte courant", compte: "4621", compteLibelle: "Compte courant associé", libelle: "Apport compte courant", tauxTva: 0 },
  subvention_aide: { label: "Subvention / Aide", compte: "71", compteLibelle: "Subventions d'exploitation", libelle: "Subvention", tauxTva: 0 },
  pret_bancaire: { label: "Prêt bancaire", compte: "164", compteLibelle: "Emprunts auprès des établissements de crédit", libelle: "Encaissement prêt", tauxTva: 0 },
  virement_interne: { label: "Virement interne", compte: "", compteLibelle: "", libelle: "Virement interne", tauxTva: 0 },
  autre: { label: "Autre", compte: "", compteLibelle: "", libelle: "", tauxTva: 0 },
};

export function tresoreriePresetFor(txType: "entree" | "sortie", t: TypeTresorerieDirecte): TresoreriePresetRow | undefined {
  if (txType === "entree") {
    if (Object.prototype.hasOwnProperty.call(TRESORERIE_PRESETS_ENTREE, t)) {
      return TRESORERIE_PRESETS_ENTREE[t as keyof typeof TRESORERIE_PRESETS_ENTREE];
    }
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(TRESORERIE_PRESETS_SORTIE, t)) {
    return TRESORERIE_PRESETS_SORTIE[t as keyof typeof TRESORERIE_PRESETS_SORTIE];
  }
  return undefined;
}

export interface TransactionRow {
  id: string;
  dossier_id: string | null;
  user_id: string;
  date: string;
  libelle: string;
  montant_ttc: number;
  type: "entree" | "sortie";
  operateur: "orange_money" | "mtn" | "wave" | "manuel" | "banque";
  statut: "non_affectee" | "affectee";
  /** Workflow manuel (complémentaire au statut affectation / justificatif). */
  statut_traitement?: string | null;
  statut_justification?: "non_justifiee" | "justifiee" | null;
  piece_nom?: string | null;
  piece_url?: string | null;
  piece_type?: string | null;
  piece_taille?: number | null;
  mode_traitement?: ModeTraitement | null;
  type_tresorerie_directe?: TypeTresorerieDirecte | null;
  compte_comptable_treso?: string | null;
  libelle_comptable_treso?: string | null;
  taux_tva_treso?: number | null;
  /** Suggestion tiers (ex. Gemini). NULL = pas encore analysé ; chaîne (éventuellement vide) = déjà traité. */
  suggested_tier?: string | null;
}

export interface TransactionLigneRow {
  id: string;
  transaction_id: string;
  categorie_id: string;
  compte_numero: string;
  compte_libelle: string;
  libelle: string;
  montant_ht: number;
  montant_tva: number;
  taux_tva: number;
  ordre: number;
}

export interface TransactionWithLignes extends TransactionRow {
  transaction_lignes?: TransactionLigneRow[];
  /**
   * Compte de charge issu de la facture d'achat rapprochée (metadata, fournisseur ou écriture D 6xx).
   * Rempli en batch par {@link fetchTransactionsForCompta}.
   */
  facture_achat_charge_compte?: { numero: string; libelle: string } | null;
}

/** Récupère les transactions du dossier (ou sans dossier si null) avec leurs lignes */
export async function fetchTransactions(
  dossierId: string | null,
  opts?: { dateDebut?: string; dateFin?: string }
): Promise<TransactionWithLignes[]> {
  let q = supabase
    .from("transactions")
    .select("*, transaction_lignes(*)")
    .order("date", { ascending: false });

  if (dossierId) {
    q = q.eq("dossier_id", dossierId);
  } else {
    q = q.is("dossier_id", null);
  }

  const d0 = opts?.dateDebut?.slice(0, 10);
  const d1 = opts?.dateFin?.slice(0, 10);
  if (d0) q = q.gte("date", d0);
  if (d1) q = q.lte("date", d1);

  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((t) => ({
    ...t,
    transaction_lignes: t.transaction_lignes || [],
  }));
}

const BATCH_IN_SIZE = 200;

function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

type MetadataFactureAchat = { compte_charge_numero?: string | null };

function compteChargeDepuisMetadata(metadata: unknown): { numero: string; libelle: string } | null {
  const raw = (metadata as MetadataFactureAchat | null)?.compte_charge_numero;
  const n = (raw ?? "").toString().trim().replace(/\D/g, "");
  if (n.length < 2) return null;
  const formatted = formatCompte8(n) || n;
  return { numero: formatted, libelle: "Charge (facture)" };
}

/** Ligne d'écriture : débit classe 6 (hors TVA 445x). */
function pickCompteChargeDebit6xx(
  lignes: { compte_numero: string; compte_libelle: string | null; debit: number; credit: number }[]
): { numero: string; libelle: string } | null {
  const rows = (lignes || []).filter((l) => {
    const d = Number(l.debit) || 0;
    if (d <= 0) return false;
    const num = String(l.compte_numero ?? "").replace(/\D/g, "");
    if (num.length < 2) return false;
    return num.startsWith("6");
  });
  if (rows.length === 0) return null;
  rows.sort((a, b) => (Number(b.debit) || 0) - (Number(a.debit) || 0));
  const top = rows[0];
  return {
    numero: String(top.compte_numero).trim(),
    libelle: String(top.compte_libelle ?? "").trim() || "Charge",
  };
}

/**
 * Pour chaque transaction avec rapprochement facture achat : déduit le compte de charge
 * (metadata, fiche fournisseur, ou écriture comptable) — requêtes batch (pas N+1).
 */
export async function enrichTransactionsContrepartieFactureAchat(
  txs: TransactionWithLignes[],
  dossierId: string
): Promise<TransactionWithLignes[]> {
  if (txs.length === 0) return txs;
  const txIds = [...new Set(txs.map((t) => t.id).filter(Boolean))];
  if (txIds.length === 0) return txs;

  /** transaction_id → facture_achat_id (priorité au plus gros montant rapproché sur une ligne FAR). */
  const txToFactureBest = new Map<string, string>();
  for (const batch of chunkIds(txIds, BATCH_IN_SIZE)) {
    const { data: farRows, error: farErr } = await supabase
      .from("facture_achat_rapprochements")
      .select("transaction_id, facture_achat_id, montant")
      .in("transaction_id", batch);
    if (farErr) {
      logger.warn("[transactions] enrich contrepartie FAR:", farErr.message);
      continue;
    }
    const byTx = new Map<string, { facture_achat_id: string; montant: number }[]>();
    for (const r of farRows || []) {
      const tid = (r as { transaction_id?: string }).transaction_id;
      const fid = (r as { facture_achat_id?: string }).facture_achat_id;
      const m = Number((r as { montant?: number }).montant) || 0;
      if (!tid || !fid) continue;
      const arr = byTx.get(tid) ?? [];
      arr.push({ facture_achat_id: fid, montant: m });
      byTx.set(tid, arr);
    }
    for (const [tid, arr] of byTx) {
      let best = arr[0];
      for (const x of arr) {
        if (x.montant > best.montant) best = x;
      }
      txToFactureBest.set(tid, best.facture_achat_id);
    }
  }

  const factureIds = [...new Set([...txToFactureBest.values()])];
  if (factureIds.length === 0) {
    return txs.map((t) => ({ ...t, facture_achat_charge_compte: null }));
  }

  const factureMeta = new Map<string, { metadata: unknown; fournisseur_fk: string | null }>();
  for (const batch of chunkIds(factureIds, BATCH_IN_SIZE)) {
    const { data: faRows, error: faErr } = await supabase
      .from("factures_achat")
      .select("id, metadata, fournisseur_fk")
      .eq("dossier_id", dossierId)
      .in("id", batch);
    if (faErr) {
      logger.warn("[transactions] enrich contrepartie factures_achat:", faErr.message);
      continue;
    }
    for (const row of faRows || []) {
      const id = (row as { id: string }).id;
      factureMeta.set(id, {
        metadata: (row as { metadata?: unknown }).metadata,
        fournisseur_fk: (row as { fournisseur_fk?: string | null }).fournisseur_fk ?? null,
      });
    }
  }

  const chargeByFacture = new Map<string, { numero: string; libelle: string }>();
  const needEcriture = new Set<string>();

  const fournisseurIds = new Set<string>();
  for (const fid of factureIds) {
    const meta = factureMeta.get(fid);
    const fromMeta = meta ? compteChargeDepuisMetadata(meta.metadata) : null;
    if (fromMeta) {
      chargeByFacture.set(fid, fromMeta);
      continue;
    }
    if (meta?.fournisseur_fk) fournisseurIds.add(meta.fournisseur_fk);
    else needEcriture.add(fid);
  }

  if (fournisseurIds.size > 0) {
    for (const batch of chunkIds([...fournisseurIds], BATCH_IN_SIZE)) {
      const { data: fRows, error: fErr } = await supabase
        .from("fournisseurs")
        .select("id, compte_charge_numero, nom")
        .eq("dossier_id", dossierId)
        .in("id", batch);
      if (fErr) continue;
      const byId = new Map((fRows || []).map((r) => [(r as { id: string }).id, r as { compte_charge_numero?: string | null; nom?: string | null }]));
      for (const fid of factureIds) {
        if (chargeByFacture.has(fid)) continue;
        const meta = factureMeta.get(fid);
        const fk = meta?.fournisseur_fk;
        if (!fk) continue;
        const f = byId.get(fk);
        const digits = (f?.compte_charge_numero ?? "").toString().trim().replace(/\D/g, "");
        if (digits.length >= 2) {
          const num = formatCompte8(digits) || digits;
          chargeByFacture.set(fid, {
            numero: num,
            libelle: (f?.nom ?? "").toString().trim() || "Fournisseur",
          });
        } else {
          needEcriture.add(fid);
        }
      }
    }
  }

  for (const fid of factureIds) {
    if (!chargeByFacture.has(fid)) needEcriture.add(fid);
  }

  if (needEcriture.size > 0) {
    const ecritureByFacture = new Map<string, { id: string }>();
    for (const batch of chunkIds([...needEcriture], BATCH_IN_SIZE)) {
      const { data: ecRows, error: ecErr } = await supabase
        .from("ecritures_comptables")
        .select("id, source_id, event_type")
        .eq("source_type", "facture_achat")
        .in("source_id", batch)
        .in("event_type", ["facture_achat_validee", "quittance_loyer_facture_achat"]);
      if (ecErr) continue;
      const byFacture = new Map<string, { id: string; event_type: string }[]>();
      for (const r of ecRows || []) {
        const sid = (r as { source_id?: string }).source_id;
        const id = (r as { id?: string }).id;
        const et = String((r as { event_type?: string }).event_type || "");
        if (!sid || !id) continue;
        const arr = byFacture.get(sid) ?? [];
        arr.push({ id, event_type: et });
        byFacture.set(sid, arr);
      }
      for (const [sid, arr] of byFacture) {
        const pref = arr.find((x) => x.event_type === "facture_achat_validee") ?? arr[0];
        if (pref) ecritureByFacture.set(sid, { id: pref.id });
      }
    }

    const ecritureIds = [...new Set([...ecritureByFacture.values()].map((v) => v.id))];
    const lignesByEcriture = new Map<string, { compte_numero: string; compte_libelle: string | null; debit: number; credit: number }[]>();
    for (const batch of chunkIds(ecritureIds, BATCH_IN_SIZE)) {
      const { data: lignes, error: lErr } = await supabase
        .from("ecritures_comptables_lignes")
        .select("ecriture_id, compte_numero, compte_libelle, debit, credit")
        .in("ecriture_id", batch);
      if (lErr) continue;
      for (const l of lignes || []) {
        const eid = (l as { ecriture_id?: string }).ecriture_id;
        if (!eid) continue;
        const arr = lignesByEcriture.get(eid) ?? [];
        arr.push({
          compte_numero: String((l as { compte_numero?: string }).compte_numero ?? ""),
          compte_libelle: (l as { compte_libelle?: string | null }).compte_libelle ?? null,
          debit: Number((l as { debit?: number }).debit) || 0,
          credit: Number((l as { credit?: number }).credit) || 0,
        });
        lignesByEcriture.set(eid, arr);
      }
    }

    for (const fid of needEcriture) {
      if (chargeByFacture.has(fid)) continue;
      const ec = ecritureByFacture.get(fid);
      if (!ec) continue;
      const picked = pickCompteChargeDebit6xx(lignesByEcriture.get(ec.id) || []);
      if (picked) chargeByFacture.set(fid, picked);
    }
  }

  return txs.map((t) => {
    const fid = txToFactureBest.get(t.id);
    const charge = fid ? chargeByFacture.get(fid) ?? null : null;
    return { ...t, facture_achat_charge_compte: charge };
  });
}

/** Récupère les transactions pour la vue compta : dossier OU sans dossier (orphelines). */
export async function fetchTransactionsForCompta(dossierId: string): Promise<TransactionWithLignes[]> {
  const [resDossier, resOrphelines] = await Promise.all([
    supabase.from("transactions").select("*, transaction_lignes(*)").eq("dossier_id", dossierId).order("date", { ascending: false }),
    supabase.from("transactions").select("*, transaction_lignes(*)").is("dossier_id", null).order("date", { ascending: false }),
  ]);
  if (resDossier.error) throw resDossier.error;
  if (resOrphelines.error) throw resOrphelines.error;
  const seen = new Set<string>();
  const all: TransactionWithLignes[] = [];
  for (const t of [...(resDossier.data || []), ...(resOrphelines.data || [])]) {
    if (seen.has(t.id)) continue;
    seen.add(t.id);
    all.push({ ...t, transaction_lignes: t.transaction_lignes || [] });
  }
  all.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  return enrichTransactionsContrepartieFactureAchat(all, dossierId);
}

/** Récupère une transaction par ID. */
export async function fetchTransactionById(transactionId: string): Promise<TransactionRow | null> {
  const { data, error } = await supabase
    .from("transactions")
    .select("*")
    .eq("id", transactionId)
    .single();
  if (error || !data) return null;
  return data as TransactionRow;
}

/** Récupère les lignes d'une transaction */
export async function fetchTransactionLignes(transactionId: string): Promise<TransactionLigneRow[]> {
  const { data, error } = await supabase
    .from("transaction_lignes")
    .select("*")
    .eq("transaction_id", transactionId)
    .order("ordre");
  if (error) throw error;
  return data || [];
}

export interface CreateTransactionInput {
  date: string;
  libelle: string;
  montant: number;
  type: "entree" | "sortie";
  operateur: "orange_money" | "mtn" | "wave" | "banque" | "manuel";
}

/** Crée une transaction (avec dossier_id si fourni) */
export async function createTransaction(
  dossierId: string | null,
  input: CreateTransactionInput
): Promise<TransactionRow> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Non connecté");

  const payload: Record<string, unknown> = {
    user_id: userData.user.id,
    date: input.date,
    libelle: input.libelle,
    montant_ttc: input.montant,
    type: input.type,
    operateur: input.operateur,
    statut: "non_affectee",
  };
  if (dossierId) payload.dossier_id = dossierId;

  const { data, error } = await supabase
    .from("transactions")
    .insert(payload)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export interface UpdateTresorerieDirecteInput {
  mode_traitement: "tresorerie_directe";
  type_tresorerie_directe: TypeTresorerieDirecte;
  compte_comptable_treso: string;
  libelle_comptable_treso: string;
  taux_tva_treso?: number;
}

/** Paramètres justificatif (toggle Compta, réservé trésorerie directe). */
export async function updateTransactionJustificatifCompta(
  transactionId: string,
  patch: { justificatif_requis: boolean; justificatif_statut: JustificatifStatut }
): Promise<{ statut_justification?: "justifiee" | "non_justifiee" }> {
  const { data: row, error: fetchErr } = await supabase
    .from("transactions")
    .select("mode_traitement, type_tresorerie_directe, compte_comptable_treso, libelle_comptable_treso")
    .eq("id", transactionId)
    .single();
  if (fetchErr) throw fetchErr;

  const base: Record<string, unknown> = {
    justificatif_requis: patch.justificatif_requis,
    justificatif_statut: patch.justificatif_statut,
  };

  if (row?.mode_traitement !== "tresorerie_directe") {
    if (patch.justificatif_statut === "non_requis") {
      base.statut_justification = "justifiee";
    }
    const { error } = await supabase.from("transactions").update(base).eq("id", transactionId);
    if (error) throw error;
    return patch.justificatif_statut === "non_requis"
      ? { statut_justification: "justifiee" }
      : {};
  }

  let statut_justification: "justifiee" | "non_justifiee";
  if (patch.justificatif_statut === "non_requis" || patch.justificatif_statut === "fourni") {
    statut_justification = "justifiee";
  } else {
    statut_justification = "non_justifiee";
  }

  const { error } = await supabase
    .from("transactions")
    .update({ ...base, statut_justification })
    .eq("id", transactionId);
  if (error) throw error;
  return { statut_justification };
}

function safeStorageFileNameJustif(file: File): string {
  const raw = file.name.replace(/[/\\]/g, "_");
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]/g, "_") || "piece";
  return `${Date.now()}_${cleaned}`;
}

export interface UploadTransactionPieceResult {
  piece_nom: string;
  piece_url: string;
  piece_type: string;
  piece_taille: number;
  justificatif_statut?: JustificatifStatut;
}

/** Upload pièce sur storage + mise à jour `transactions` (optionnellement `justificatif_statut = fourni`). */
export async function uploadTransactionPieceJustificatif(
  dossierId: string,
  transactionId: string,
  file: File,
  options?: { markJustificatifFourni?: boolean }
): Promise<UploadTransactionPieceResult> {
  const path = `${dossierId}/${transactionId}/${safeStorageFileNameJustif(file)}`;
  const contentType = file.type || "application/octet-stream";
  const { error: upErr } = await supabase.storage.from(BUCKET_PIECES_JUSTIFICATIVES).upload(path, file, {
    upsert: true,
    contentType,
  });
  if (upErr) throw upErr;
  const base = {
    piece_nom: file.name,
    piece_url: path,
    piece_type: contentType,
    piece_taille: file.size,
  };
  const dbPatch = {
    ...base,
    ...(options?.markJustificatifFourni ? { justificatif_statut: "fourni" as const } : {}),
  };
  const { error: dbErr } = await supabase.from("transactions").update(dbPatch).eq("id", transactionId);
  if (dbErr) throw dbErr;
  return dbPatch;
}

/** Met à jour une transaction en mode trésorerie directe (sans facture). Le trigger aligne statut_justification (pièce en attente → non justifiée). */
export async function updateTransactionTresorerieDirecte(
  transactionId: string,
  input: UpdateTresorerieDirecteInput
): Promise<void> {
  const treso8 = formatCompte8(input.compte_comptable_treso.trim());
  const { error } = await supabase
    .from("transactions")
    .update({
      mode_traitement: input.mode_traitement,
      type_tresorerie_directe: input.type_tresorerie_directe,
      compte_comptable_treso: treso8 || input.compte_comptable_treso.trim() || null,
      libelle_comptable_treso: input.libelle_comptable_treso || null,
      taux_tva_treso: input.taux_tva_treso ?? null,
    })
    .eq("id", transactionId);
  if (error) throw error;

  try {
    await genererEcritureTresorerieDirecteValidee(transactionId);
  } catch (err) {
    logger.error("Écriture comptable trésorerie directe non générée:", err);
    throw new Error(errorMessageFromUnknown(err));
  }
}

export interface LigneAffectationInput {
  categorieId: string;
  compte_numero: string;
  compte_libelle: string;
  libelle: string;
  montantHT: number;
  montantTVA: number;
  tauxTVA: number;
  ordre: number;
}

/** Une ligne d’affectation (catégorie « autre » par défaut) pour une transaction. */
export function buildLigneAffectationUnique(params: {
  transaction: { libelle: string; montantTTC: number; type: "entree" | "sortie" };
  compteNumero: string;
  compteLibelle: string;
  categorieId?: string;
  tvaActive: boolean;
  tauxTva: number;
}): LigneAffectationInput {
  const ttc = Math.round(Math.abs(params.transaction.montantTTC));
  const taux = params.tvaActive ? params.tauxTva : 0;
  const ht = params.tvaActive && taux > 0 ? Math.round(ttc / (1 + taux / 100)) : ttc;
  const montantTVA = ttc - ht;
  const n8 = formatCompte8(params.compteNumero.trim());
  return {
    categorieId: params.categorieId ?? "autre",
    compte_numero: n8 || params.compteNumero.trim(),
    compte_libelle: params.compteLibelle.trim() || params.compteNumero.trim(),
    libelle: params.transaction.libelle,
    montantHT: ht,
    montantTVA,
    tauxTVA: taux,
    ordre: 0,
  };
}

export async function updateTransactionSuggestedTier(transactionId: string, suggestedTier: string | null): Promise<void> {
  const { error } = await supabase.from("transactions").update({ suggested_tier: suggestedTier }).eq("id", transactionId);
  if (error) throw error;
}

const MONTANT_TOLERANCE = 50; // FCFA (arrondis, décimales)

/** Récupère les transactions du dossier OU sans dossier (dossier_id null) pour la recherche rapprochement. */
async function fetchTransactionsForSearch(dossierId: string): Promise<TransactionRow[]> {
  const { data, error } = await supabase
    .from("transactions")
    .select("id, dossier_id, user_id, date, libelle, montant_ttc, type, operateur, statut, piece_nom, piece_url, piece_type, piece_taille, mode_traitement, type_tresorerie_directe")
    .or(`dossier_id.eq.${dossierId},dossier_id.is.null`)
    .order("date", { ascending: false });
  if (error) throw error;
  return (data || []) as TransactionRow[];
}

/** IDs des transactions déjà rapprochées (dans facture_achat_rapprochements). */
async function fetchTransactionIdsRapprochees(): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("facture_achat_rapprochements")
    .select("transaction_id");
  if (error) throw error;
  const ids = (data || []).map((r) => (r as { transaction_id: string }).transaction_id).filter(Boolean);
  return new Set(ids);
}

/** Map transaction_id → somme des montants déjà rapprochés (achat + import vente, pour recherche multi-factures). */
export async function fetchSommesRapprochementsParTransaction(
  transactionIds: string[]
): Promise<Map<string, number>> {
  if (transactionIds.length === 0) return new Map();
  const map = new Map<string, number>();
  const addRows = (rows: { transaction_id?: string; montant?: number }[] | null) => {
    for (const r of rows || []) {
      const tid = r.transaction_id;
      if (!tid) continue;
      const m = Number(r.montant) || 0;
      map.set(tid, (map.get(tid) ?? 0) + m);
    }
  };
  const { data: achatRows, error: e1 } = await supabase
    .from("facture_achat_rapprochements")
    .select("transaction_id, montant")
    .in("transaction_id", transactionIds);
  if (e1) throw e1;
  addRows(achatRows as { transaction_id?: string; montant?: number }[]);
  const { data: fviRows, error: e2 } = await supabase
    .from("facture_vente_import_rapprochements")
    .select("transaction_id, montant")
    .in("transaction_id", transactionIds);
  if (e2) throw e2;
  addRows(fviRows as { transaction_id?: string; montant?: number }[]);
  return map;
}

/** Somme des montants déjà rapprochés pour une facture. */
async function fetchSommeRapprochementsFacture(factureId: string): Promise<number> {
  const { data, error } = await supabase
    .from("facture_achat_rapprochements")
    .select("montant")
    .eq("facture_achat_id", factureId);
  if (error) throw error;
  return (data || []).reduce((s, r) => s + (Number((r as { montant: number }).montant) || 0), 0);
}

/** Seuil minimum : montant transaction >= X% du reste facture pour éviter candidats absurdes (ex: 250 FCFA pour 601k). */
const SEUIL_MONTANT_MIN_RAPPORT = 0.05;
/** Fenêtre temporelle : transaction dans ± X jours de la date facture. `Infinity` = pas de filtre temporel. */
const FENETRE_JOURS_FACTURE = Infinity;

/** Tolérance FCFA pour considérer le disponible transaction = reste à payer facture (match « exact »). */
const MATCH_EXACT_MONTANT_TOLERANCE = 1;

/** Résultat de recherche transaction pour rapprochement facture d'achat (tri + indicateur match montant). */
export interface TransactionSearchResultForFacture extends TransactionRow {
  matchExact: boolean;
}

/** Recherche des transactions compatibles pour rapprochement. Critères stricts : cohérence montant, fournisseur, temporalité. */
export async function searchTransactionsForFacture(
  dossierId: string,
  montantFacture: number,
  libelleOptional?: string,
  factureId?: string,
  dateEmissionOptional?: string
): Promise<TransactionSearchResultForFacture[]> {
  let montantRestant = montantFacture;
  if (factureId) {
    const sommeRapprochee = await fetchSommeRapprochementsFacture(factureId);
    montantRestant = Math.max(0, montantFacture - sommeRapprochee);
  }
  if (montantRestant <= 0) return [];

  const all = await fetchTransactionsForSearch(dossierId);
  const sorties = all.filter((t) => t.type === "sortie");
  const txIds = sorties.map((t) => t.id);
  const sommesTx = await fetchSommesRapprochementsParTransaction(txIds);

  const norm = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const matchFournisseur = (t: TransactionRow) => {
    if (!libelleOptional?.trim()) return false;
    const words = libelleOptional.trim().toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
    const lib = norm(t.libelle || "");
    return words.some((w) => lib.includes(norm(w)));
  };

  const inFenetreTemporelle = (txDate: string) => {
    if (FENETRE_JOURS_FACTURE === Infinity) return true;
    if (!dateEmissionOptional?.trim()) return true;
    const dFacture = new Date(dateEmissionOptional);
    const dTx = new Date(txDate);
    const diffJours = Math.abs((dTx.getTime() - dFacture.getTime()) / (1000 * 60 * 60 * 24));
    return diffJours <= FENETRE_JOURS_FACTURE;
  };

  const montantMin = montantRestant * SEUIL_MONTANT_MIN_RAPPORT;

  const estTresorerieDirecteFrais = (t: TransactionRow) =>
    t.mode_traitement === "tresorerie_directe" &&
    (t.type_tresorerie_directe === "agios" || t.type_tresorerie_directe === "frais_bancaires" || t.type_tresorerie_directe === "commissions");

  const list = sorties.filter((t) => {
    if (estTresorerieDirecteFrais(t)) return false;
    const m = Math.abs(Number(t.montant_ttc) || 0);
    if (m <= 0) return false;
    const dejaRapproche = sommesTx.get(t.id) ?? 0;
    const montantRestantTx = Math.max(0, m - dejaRapproche);
    if (montantRestantTx <= 0) return false;
    if (montantRestantTx < montantMin) return false;
    if (!inFenetreTemporelle(t.date || "")) return false;
    return true;
  });

  const montantRestantRounded = Math.round(montantRestant);

  const enriched: TransactionSearchResultForFacture[] = list.map((t) => {
    const m = Math.abs(Number(t.montant_ttc) || 0);
    const dejaRapproche = sommesTx.get(t.id) ?? 0;
    const montantRestantTx = Math.max(0, m - dejaRapproche);
    const matchExact =
      Math.abs(Math.round(montantRestantTx) - montantRestantRounded) <= MATCH_EXACT_MONTANT_TOLERANCE;
    return { ...t, matchExact };
  });

  const tier = (row: TransactionSearchResultForFacture): number => {
    if (row.matchExact) return 0;
    if (matchFournisseur(row)) return 1;
    return 2;
  };

  enriched.sort((a, b) => {
    const ta = tier(a);
    const tb = tier(b);
    if (ta !== tb) return ta - tb;
    return (b.date || "").localeCompare(a.date || "");
  });

  return enriched;
}

/** Somme des montants déjà rapprochés sur une facture vente importée. */
export async function fetchSommeRapprochementsFactureVenteImport(importId: string): Promise<number> {
  const { data, error } = await supabase
    .from("facture_vente_import_rapprochements")
    .select("montant")
    .eq("facture_vente_import_id", importId);
  if (error) throw error;
  return (data || []).reduce((s, r) => s + (Number((r as { montant: number }).montant) || 0), 0);
}

/** Carte import_id → somme rapprochée (requête groupée pour les listes). */
export async function fetchSommesRapprochementsParFacturesVenteImport(importIds: string[]): Promise<Map<string, number>> {
  if (importIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from("facture_vente_import_rapprochements")
    .select("facture_vente_import_id, montant")
    .in("facture_vente_import_id", importIds);
  if (error) throw error;
  const map = new Map<string, number>();
  for (const r of data || []) {
    const id = (r as { facture_vente_import_id: string }).facture_vente_import_id;
    const m = Number((r as { montant: number }).montant) || 0;
    map.set(id, (map.get(id) ?? 0) + m);
  }
  return map;
}

/** Recherche transactions entrantes pour rapprocher un encaissement client (facture vente importée). */
export async function searchTransactionsForFactureVenteImport(
  dossierId: string,
  montantFacture: number,
  libelleClientOptional?: string,
  importId?: string,
  dateEmissionOptional?: string
): Promise<TransactionSearchResultForFacture[]> {
  let montantRestant = montantFacture;
  if (importId) {
    const sommeRapprochee = await fetchSommeRapprochementsFactureVenteImport(importId);
    montantRestant = Math.max(0, montantFacture - sommeRapprochee);
  }
  if (montantRestant <= 0) return [];

  const all = await fetchTransactionsForSearch(dossierId);
  const entrees = all.filter((t) => t.type === "entree");
  const txIds = entrees.map((t) => t.id);
  const sommesTx = await fetchSommesRapprochementsParTransaction(txIds);

  const norm = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const matchClient = (t: TransactionRow) => {
    if (!libelleClientOptional?.trim()) return false;
    const words = libelleClientOptional.trim().toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
    const lib = norm(t.libelle || "");
    const tier = norm((t as { suggested_tier?: string | null }).suggested_tier || "");
    return words.some((w) => lib.includes(norm(w)) || (tier && tier.includes(norm(w))));
  };

  const inFenetreTemporelle = (txDate: string) => {
    if (FENETRE_JOURS_FACTURE === Infinity) return true;
    if (!dateEmissionOptional?.trim()) return true;
    const dFacture = new Date(dateEmissionOptional);
    const dTx = new Date(txDate);
    const diffJours = Math.abs((dTx.getTime() - dFacture.getTime()) / (1000 * 60 * 60 * 24));
    return diffJours <= FENETRE_JOURS_FACTURE;
  };

  const montantMin = montantRestant * SEUIL_MONTANT_MIN_RAPPORT;

  const estTresorerieDirecteFrais = (t: TransactionRow) =>
    t.mode_traitement === "tresorerie_directe" &&
    (t.type_tresorerie_directe === "agios" || t.type_tresorerie_directe === "frais_bancaires" || t.type_tresorerie_directe === "commissions");

  const list = entrees.filter((t) => {
    if (estTresorerieDirecteFrais(t)) return false;
    const m = Math.abs(Number(t.montant_ttc) || 0);
    if (m <= 0) return false;
    const dejaRapproche = sommesTx.get(t.id) ?? 0;
    const montantRestantTx = Math.max(0, m - dejaRapproche);
    if (montantRestantTx <= 0) return false;
    if (montantRestantTx < montantMin) return false;
    if (!inFenetreTemporelle(t.date || "")) return false;
    return true;
  });

  const montantRestantRounded = Math.round(montantRestant);

  const enriched: TransactionSearchResultForFacture[] = list.map((t) => {
    const m = Math.abs(Number(t.montant_ttc) || 0);
    const dejaRapproche = sommesTx.get(t.id) ?? 0;
    const montantRestantTx = Math.max(0, m - dejaRapproche);
    const matchExact =
      Math.abs(Math.round(montantRestantTx) - montantRestantRounded) <= MATCH_EXACT_MONTANT_TOLERANCE;
    return { ...t, matchExact };
  });

  const tier = (row: TransactionSearchResultForFacture): number => {
    if (row.matchExact) return 0;
    if (matchClient(row)) return 1;
    return 2;
  };

  enriched.sort((a, b) => {
    const ta = tier(a);
    const tb = tier(b);
    if (ta !== tb) return ta - tb;
    return (b.date || "").localeCompare(a.date || "");
  });

  return enriched;
}

/** Enregistre un rapprochement import vente ↔ transaction entrante (déclenche triggers metadata + transaction). */
export async function rapprocherFactureVenteImport(importId: string, transactionId: string, montant: number): Promise<void> {
  const m = Math.round(Math.abs(Number(montant)) || 0);
  if (m <= 0) throw new Error("Montant de rapprochement invalide");

  const { error } = await supabase.from("facture_vente_import_rapprochements").upsert(
    { facture_vente_import_id: importId, transaction_id: transactionId, montant: m },
    { onConflict: "facture_vente_import_id,transaction_id" }
  );
  if (error) throw error;
}

export async function fetchRapprochementsFactureVenteImport(importId: string): Promise<RapprochementFactureRow[]> {
  const { data, error } = await supabase
    .from("facture_vente_import_rapprochements")
    .select("transaction_id, montant")
    .eq("facture_vente_import_id", importId);
  if (error) throw error;
  const rows = (data || []) as { transaction_id: string; montant: number }[];
  const out: RapprochementFactureRow[] = [];
  for (const r of rows) {
    const tx = await fetchTransactionById(r.transaction_id);
    out.push({ transaction_id: r.transaction_id, montant: Number(r.montant) || 0, transaction: tx ?? undefined });
  }
  return out;
}

export async function detacherRapprochementFactureVenteImport(importId: string, transactionId: string): Promise<void> {
  const { error } = await supabase
    .from("facture_vente_import_rapprochements")
    .delete()
    .eq("facture_vente_import_id", importId)
    .eq("transaction_id", transactionId);
  if (error) throw error;
}

export async function detacherTousRapprochementsFactureVenteImport(importId: string): Promise<void> {
  const { error } = await supabase.from("facture_vente_import_rapprochements").delete().eq("facture_vente_import_id", importId);
  if (error) throw error;
}

/** Rapproche une facture d'achat avec une transaction (lien paiement). Insère dans facture_achat_rapprochements ; triggers (statut_rapprochement, statut_justification, statut affectee) + sync restant/statut facture. */
export async function rapprocherFactureAchat(
  factureId: string,
  transactionId: string,
  montant: number
): Promise<void> {
  const m = Math.round(Math.abs(Number(montant)) || 0);
  if (m <= 0) throw new Error("Montant de rapprochement invalide");

  const { error } = await supabase
    .from("facture_achat_rapprochements")
    .upsert(
      { facture_achat_id: factureId, transaction_id: transactionId, montant: m },
      { onConflict: "facture_achat_id,transaction_id" }
    );
  if (error) throw error;

  try {
    await genererEcritureTransactionDepenseRapprochee(factureId, transactionId, m);
  } catch (err) {
    logger.error("Écriture comptable paiement non générée:", err);
  }

  try {
    const { syncFactureAchatPaiementEtStatut } = await import("./gestion/facturesAchat");
    await syncFactureAchatPaiementEtStatut(factureId);
  } catch (err) {
    logger.error("Sync statut / restant facture achat:", err);
  }
}

/** Détache la transaction liée à une facture d'achat. Supprime le(s) rapprochement(s) ; les triggers recalculent les statuts. */
export async function detacherFactureAchat(factureId: string): Promise<void> {
  const { error } = await supabase
    .from("facture_achat_rapprochements")
    .delete()
    .eq("facture_achat_id", factureId);
  if (error) throw error;
}

export interface RapprochementFactureRow {
  transaction_id: string;
  montant: number;
  transaction?: TransactionRow | null;
}

/** Rapprochements d'une facture d'achat (transaction_id, montant). */
export async function fetchRapprochementsFacture(factureId: string): Promise<RapprochementFactureRow[]> {
  const { data, error } = await supabase
    .from("facture_achat_rapprochements")
    .select("transaction_id, montant")
    .eq("facture_achat_id", factureId);
  if (error) throw error;
  const rows = (data || []) as { transaction_id: string; montant: number }[];
  const out: RapprochementFactureRow[] = [];
  for (const r of rows) {
    const tx = await fetchTransactionById(r.transaction_id);
    out.push({ transaction_id: r.transaction_id, montant: Number(r.montant) || 0, transaction: tx ?? undefined });
  }
  return out;
}

/** Détache un rapprochement spécifique (facture + transaction). */
export async function detacherRapprochementTransaction(factureId: string, transactionId: string): Promise<void> {
  const { error } = await supabase
    .from("facture_achat_rapprochements")
    .delete()
    .eq("facture_achat_id", factureId)
    .eq("transaction_id", transactionId);
  if (error) throw error;
}

export interface FactureAchatRow {
  id: string;
  fournisseur_nom: string;
  numero: string;
  montant_ttc: number;
  /** Restant à payer (TTC), aligné sur les rapprochements si sync OK. */
  restant_du_ttc?: number | null;
  date_emission: string;
  transaction_id?: string | null;
  file_url?: string | null;
}

/** Somme des montants déjà rapprochés pour une transaction (multi-factures). */
export async function fetchSommeRapprochementsTransaction(transactionId: string): Promise<number> {
  const { data, error } = await supabase
    .from("facture_achat_rapprochements")
    .select("montant")
    .eq("transaction_id", transactionId);
  if (error) throw error;
  return (data || []).reduce((s, r) => s + (Number((r as { montant: number }).montant) || 0), 0);
}

export interface FactureAchatRapprochee {
  facture: FactureAchatRow;
  montant: number;
}

/** Récupère toutes les factures d'achat liées à une transaction avec leur montant. */
export async function fetchFacturesAchatByTransactionId(
  transactionId: string
): Promise<FactureAchatRapprochee[]> {
  const { data: farList, error: farErr } = await supabase
    .from("facture_achat_rapprochements")
    .select("facture_achat_id, montant")
    .eq("transaction_id", transactionId);
  if (farErr) throw farErr;
  const rows = (farList || []) as { facture_achat_id: string; montant: number }[];
  if (rows.length === 0) return [];

  const faIds = [...new Set(rows.map((r) => r.facture_achat_id))];
  const { data: faData, error: faErr } = await supabase
    .from("factures_achat")
    .select("id, fournisseur_nom, numero, montant_ttc, date_emission, transaction_id, file_url")
    .in("id", faIds);
  if (faErr) throw faErr;
  const facturesMap = new Map(
    (faData || []).map((f) => [(f as { id: string }).id, f as FactureAchatRow])
  );

  return rows
    .map((r) => {
      const facture = facturesMap.get(r.facture_achat_id);
      if (!facture) return null;
      return { facture, montant: Number(r.montant) || 0 };
    })
    .filter((x): x is FactureAchatRapprochee => x != null);
}

/** Répartit les factures en "suggérée" (unique ou nettement meilleure) vs "autres candidates". */
export function partitionFacturesSuggerees(
  list: FactureAchatRow[],
  fournisseurRef: string
): { suggested: FactureAchatRow | null; others: FactureAchatRow[] } {
  if (list.length === 0) return { suggested: null, others: [] };
  if (list.length === 1) return { suggested: list[0], others: [] };
  const ref = (fournisseurRef || "").trim();
  if (!ref) return { suggested: null, others: list };
  const norm = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const refNorm = norm(ref);
  const matches = list.filter((f) => {
    const nom = norm(f.fournisseur_nom || "");
    return nom.includes(refNorm) || refNorm.includes(nom);
  });
  if (matches.length === 1) {
    return { suggested: matches[0], others: list.filter((f) => f.id !== matches[0].id) };
  }
  return { suggested: null, others: list };
}

/** Contexte comptable d'une transaction pour l'affichage des écritures. Résolution légère, pas de moteur métier. */
export type TransactionComptableContext =
  | "direct"           // trésorerie directe, dépense injustifiée, affectation manuelle
  | "achat"            // sortie rapprochée à facture(s) achat
  | "vente_encaissement"   // entrée rapprochée à facture(s) ou avoir(s) compensation
  | "vente_remboursement"; // sortie rapprochée à avoir(s)

/** Résout le contexte comptable d'une transaction. Utilise les fetchers existants. */
export async function resolveTransactionComptableContext(
  transactionId: string,
  txType: "entree" | "sortie"
): Promise<TransactionComptableContext> {
  const { data: rapprochementsAchat } = await supabase
    .from("facture_achat_rapprochements")
    .select("id")
    .eq("transaction_id", transactionId)
    .limit(1);
  if (rapprochementsAchat?.length) return "achat";

  const { data: paiementsVente } = await supabase
    .from("facture_paiements")
    .select("facture_id")
    .eq("transaction_id", transactionId);
  if (paiementsVente?.length) {
    const factureIds = paiementsVente.map((p) => (p as { facture_id: string }).facture_id);
    const { data: factures } = await supabase
      .from("factures")
      .select("facture_origine_id")
      .in("id", factureIds);
    const hasAvoir = (factures || []).some((f) => (f as { facture_origine_id?: string | null }).facture_origine_id);
    if (txType === "sortie" && hasAvoir) return "vente_remboursement";
    return "vente_encaissement";
  }

  return "direct";
}

/** Récupère la facture d'achat liée à une transaction (si existante). Source : facture_achat_rapprochements (compat transaction_id via trigger). */
export async function fetchFactureAchatByTransactionId(
  transactionId: string
): Promise<FactureAchatRow | null> {
  const { data: farList, error: farErr } = await supabase
    .from("facture_achat_rapprochements")
    .select("facture_achat_id")
    .eq("transaction_id", transactionId)
    .limit(1);
  if (farErr) throw farErr;
  const faId = (farList as { facture_achat_id?: string }[] | null)?.[0]?.facture_achat_id;
  if (!faId) return null;

  const { data, error } = await supabase
    .from("factures_achat")
    .select("id, fournisseur_nom, numero, montant_ttc, date_emission, transaction_id")
    .eq("id", faId)
    .single();
  if (error || !data) return null;
  return data as FactureAchatRow;
}

/** IDs des transactions du dossier ayant une facture d'achat rapprochée. Source : facture_achat_rapprochements. */
export async function fetchTransactionIdsWithFactureRapprochee(
  dossierId: string
): Promise<Set<string>> {
  const { data: factures, error: faErr } = await supabase
    .from("factures_achat")
    .select("id")
    .eq("dossier_id", dossierId);
  if (faErr) throw faErr;
  const faIds = (factures || []).map((f) => (f as { id: string }).id);
  if (faIds.length === 0) return new Set();

  const { data, error } = await supabase
    .from("facture_achat_rapprochements")
    .select("transaction_id")
    .in("facture_achat_id", faIds);
  if (error) throw error;
  const ids = (data || [])
    .map((r) => (r as { transaction_id: string }).transaction_id)
    .filter(Boolean);
  return new Set(ids);
}

/** Options pour la recherche multi-factures. */
export interface SearchFacturesAchatOptions {
  /** Si true et sans transactionId : filtre SQL sur montant_ttc (ancien comportement). */
  useMontantRestant?: boolean;
  /** Référence montant = |montantRef| − Σ rapprochements sur cette transaction. */
  transactionId?: string;
}

/** Recherche factures d'achat non soldées : restant TTC proche du montant (ou du restant transaction si transactionId). Filtre optionnel libellé / fournisseur. */
export async function searchFacturesAchatForTransaction(
  dossierId: string,
  montantRef: number,
  libelleOptional?: string,
  options?: SearchFacturesAchatOptions
): Promise<FactureAchatRow[]> {
  let ref = Math.abs(Number(montantRef) || 0);
  if (options?.transactionId) {
    const s = await fetchSommeRapprochementsTransaction(options.transactionId);
    ref = Math.max(0, ref - s);
  }
  if (ref < 0.01) return [];

  const useLegacyBand = options?.useMontantRestant === true && !options?.transactionId;
  const tol = MONTANT_TOLERANCE;
  const refRounded = Math.round(ref);

  let q = supabase
    .from("factures_achat")
    .select("id, fournisseur_nom, numero, montant_ttc, restant_du_ttc, date_emission, transaction_id")
    .eq("dossier_id", dossierId)
    .neq("statut", "payee")
    .neq("statut", "archive")
    .order("date_emission", { ascending: false })
    .limit(200);

  if (useLegacyBand) {
    q = q.gte("montant_ttc", 0.01).lte("montant_ttc", ref + tol);
  }

  const { data, error } = await q;
  if (error) throw error;
  let list = (data || []) as FactureAchatRow[];

  if (!useLegacyBand) {
    list = list.filter((f) => {
      const rest =
        f.restant_du_ttc != null && !Number.isNaN(Number(f.restant_du_ttc))
          ? Number(f.restant_du_ttc)
          : Math.max(0, Number(f.montant_ttc) || 0);
      if (rest < 0.01) return false;
      return Math.abs(Math.round(rest) - refRounded) <= tol;
    });
  }

  if (libelleOptional && libelleOptional.trim()) {
    const words = libelleOptional.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const normalized = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const filteredByLibelle = list.filter((f) => {
      const nom = normalized(f.fournisseur_nom || "");
      return words.some((w) => nom.includes(normalized(w)));
    });
    if (filteredByLibelle.length > 0) list = filteredByLibelle;
  }
  return list;
}

/** Noms distincts des fournisseurs du dossier (factures d'achat) pour le matching tiers. */
export async function fetchFournisseurNoms(dossierId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("factures_achat")
    .select("fournisseur_nom")
    .eq("dossier_id", dossierId)
    .not("fournisseur_nom", "is", null);
  if (error) throw error;
  const names = (data || [])
    .map((r) => (r as { fournisseur_nom: string | null }).fournisseur_nom?.trim())
    .filter((n): n is string => !!n && n.length > 0);
  return [...new Set(names)];
}

/** Extrait le nom du tiers (fournisseur ou client) depuis le libellé d'opération bancaire / mobile money via Gemini. */
export async function matchTierFromLibelle(
  dossierId: string,
  libelle: string,
  type: "entree" | "sortie"
): Promise<{ name: string | null }> {
  try {
    const { data, error } = await supabase.functions.invoke("match-tier-transaction", {
      body: { libelle, type, dossier_id: dossierId },
    });
    if (error) return { name: null }; // ← ne pas throw
    const ok = data?.ok === true;
    const name = ok && typeof data?.name === "string" ? data.name : null;
    return { name };
  } catch {
    return { name: null }; // ← filet de sécurité
  }
}

/** Affecte une transaction (statut + lignes) via RPC atomique (Audit 2). */
export async function affecterTransaction(
  transactionId: string,
  lignes: LigneAffectationInput[],
  piece?: { nom: string; type: string; url: string; taille: number }
): Promise<void> {
  const pLignes = lignes.map((l) => ({
    categorie_id: l.categorieId,
    compte_numero: formatCompte8(l.compte_numero.trim()) || l.compte_numero.trim(),
    compte_libelle: l.compte_libelle,
    libelle: l.libelle,
    montant_ht: l.montantHT,
    montant_tva: l.montantTVA,
    taux_tva: l.tauxTVA,
  }));
  const { error } = await supabase.rpc("affecter_transaction", {
    p_transaction_id: transactionId,
    p_lignes: pLignes,
    p_piece_nom: piece?.nom ?? null,
    p_piece_url: piece?.url ?? null,
    p_piece_type: piece?.type ?? null,
    p_piece_taille: piece?.taille ?? null,
  });
  if (error) throw error;
}
